import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Mail, Home } from 'lucide-react';

export default function OrderSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderCode, paymentInfo, customerEmail } = location.state || {};

  const isCard = paymentInfo?.method === 'card';

  useEffect(() => {
    if (!orderCode && !isCard) {
      navigate('/');
    }
  }, []);

  if (!orderCode && !isCard) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-10 px-4">
      <div className="max-w-lg mx-auto space-y-4">

        {/* Header */}
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900">Order Confirmed!</h1>
          <p className="text-gray-500 text-sm mt-1">Thank you for choosing GrowProPlus</p>
        </div>

        {/* Order Code */}
        {orderCode ? (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Order Reference</p>
            <p className="text-2xl font-extrabold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              {orderCode}
            </p>
          </div>
        ) : isCard && (
          <div className="bg-green-50 rounded-2xl p-5 shadow-sm border border-green-200">
            <p className="text-xs text-green-700 font-semibold uppercase tracking-wide mb-1">Payment Confirmed</p>
            <p className="text-sm text-green-800">Your order reference will be sent to your email once processing is complete.</p>
          </div>
        )}

        {/* Email confirmation */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">Confirmation sent by email</p>
            {customerEmail && (
              <p className="text-xs text-gray-500 mt-0.5">
                We sent the details to <span className="font-semibold text-blue-600">{customerEmail}</span>
              </p>
            )}
          </div>
        </div>

        {/* Next steps */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-bold text-gray-900 mb-3">What happens next</p>
          <div className="space-y-3">
            {[
              { n: 1, text: 'Payment processed successfully', color: 'bg-pink-500' },
              { n: 2, text: 'You will receive an email confirmation shortly', color: 'bg-purple-500' },
              { n: 3, text: 'Our system will start processing your order', color: 'bg-blue-500' },
              { n: 4, text: 'Results will appear gradually on your platform', color: 'bg-green-500' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <span className={`${step.color} text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  {step.n}
                </span>
                <p className="text-sm text-gray-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Important note */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> Keep your profile or channel <strong>public</strong> during the entire delivery process.
          </p>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all text-sm"
          >
            <Home className="w-4 h-4" />
            Home
          </button>
          <button
            onClick={() => window.location.href = 'mailto:growproplus@growproplus.com'}
            className="flex items-center justify-center gap-2 bg-white text-gray-700 py-3 rounded-xl font-bold border-2 border-gray-200 hover:border-pink-400 hover:text-pink-600 transition-all text-sm"
          >
            Need Help?
          </button>
        </div>

      </div>
    </div>
  );
}
